import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, UploadCloud, Play, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Defensive Response reader. Shared with the rest of the app via
 * /utils/fetchSafe.js so any future PostHog-locked-stream bugs get
 * fixed in one place.
 */
import { readResponse } from '../utils/fetchSafe';

/**
 * Upload a File to the v2 import endpoint in ~512 KB chunks. Each POST stays
 * well under nginx-ingress' default 1 MB body limit (which 502s on production
 * when the full ~6 MB CSV is sent in one shot). Calls onProgress({sent,total})
 * after each chunk so the UI can show a real upload progress bar.
 */
const CHUNK_SIZE = 512 * 1024;  // 512 KB
const uploadInChunks = async (file, jobId, onProgress) => {
  const token = localStorage.getItem('token');
  const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  for (let i = 0; i < total; i++) {
    const start = i * CHUNK_SIZE;
    const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
    const form = new FormData();
    form.append('job_id', jobId);
    form.append('index', String(i));
    form.append('total', String(total));
    form.append('chunk', blob, 'chunk.bin');
    const res = await fetch(`${API_URL}/api/admin/import-projects-v2/upload-chunk`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (!res.ok) {
      const { data, text } = await readResponse(res);
      throw new Error(data?.detail || `Chunk ${i + 1}/${total} failed (HTTP ${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`);
    }
    onProgress?.({ sent: i + 1, total });
  }
};

const Stat = ({ label, value, tone = 'gray' }) => {
  const toneMap = {
    gray:   'text-gray-700',
    green:  'text-emerald-700',
    amber:  'text-amber-700',
    red:    'text-red-700',
    blue:   'text-sky-700',
    orange: 'text-orange-600',
  };
  return (
    <div className="bg-white rounded shadow p-4">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-2xl font-light ${toneMap[tone]}`}>{value}</div>
    </div>
  );
};

const ImportProjectsV2 = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState({ phase: '', pct: 0, current: 0, total: 0 });
  const pollerRef = useRef(null);
  const jobIdRef = useRef(null);

  const onPick = (e) => {
    setFile(e.target.files?.[0] || null);
    setReport(null);
    setResult(null);
  };

  const doDryRun = async () => {
    if (!file) { toast.error('Pick a CSV first.'); return; }
    setDryRunning(true);
    setReport(null);
    setResult(null);
    const jobId = `v2-dry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      // Upload in chunks first (each chunk <1 MB so nginx-ingress doesn't 502).
      setProgress({ phase: 'Uploading…', pct: 0, current: 0, total: 0 });
      await uploadInChunks(file, jobId, ({ sent, total }) => {
        setProgress({ phase: `Uploading… ${sent}/${total}`,
                       pct: Math.round((sent / total) * 100),
                       current: sent, total });
      });
      // Kick the dry-run worker — it reads from the buffer we just uploaded.
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/import-projects-v2/dry-run?job_id=${jobId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data, text } = await readResponse(res);
      const kicked = res.ok && data?.status === 'started';
      if (!kicked) {
        throw new Error(data?.detail || `Dry run failed (HTTP ${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`);
      }
      // Poll progress for the dry-run result (same in-memory job dict as apply).
      const startedAt = Date.now();
      while (Date.now() - startedAt < 300_000) {  // hard cap 5 min
        await new Promise(r => setTimeout(r, 1500));
        const pRes = await fetch(`${API_URL}/api/admin/import-projects-v2/progress/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!pRes.ok) continue;
        const { data: p } = await readResponse(pRes);
        setProgress(p);
        if (p.phase === 'Done' && p.result) {
          setReport(p.result);
          toast.success(`Dry run complete — ${p.result.would_import} projects would be imported.`);
          return;
        }
        if (p.phase === 'Error') {
          throw new Error(p.error || 'Dry run failed');
        }
      }
      throw new Error('Dry run timed out after 5 minutes');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDryRunning(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollerRef.current) clearInterval(pollerRef.current);
    pollerRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/admin/import-projects-v2/progress/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const { data: p } = await readResponse(res);
        setProgress(p);
        if (p.phase === 'Done') {
          clearInterval(pollerRef.current);
          pollerRef.current = null;
          if (p.result) {
            setResult(p.result);
            toast.success(
              `Imported ${p.result.inserted_projects} projects, ${p.result.inserted_customers} customers, ` +
              `${p.result.inserted_employees} employees, ${p.result.inserted_timeslips} timeslips.`
            );
          }
          setApplying(false);
        } else if (p.phase === 'Error') {
          clearInterval(pollerRef.current);
          pollerRef.current = null;
          toast.error(p.error || 'Import failed');
          setApplying(false);
        }
      } catch {
        // Silent — next poll will retry.
      }
    }, 1500);
  };

  const doApply = async () => {
    if (!file) { toast.error('Pick a CSV first.'); return; }
    if (!window.confirm(
      `This will import ${report?.would_import || 'all'} projects, ${report?.new_customers || ''} customers, ` +
      `${report?.skeleton_employees || ''} inactive employees, and ${report?.entries_to_attach || ''} labor/expense entries. ` +
      `This cannot be undone. Continue?`
    )) return;

    setApplying(true);
    setResult(null);
    const jobId = `v2-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    jobIdRef.current = jobId;
    setProgress({ phase: 'Uploading…', pct: 0, current: 0, total: 0 });

    try {
      // Upload the CSV in chunks first (each chunk <1 MB so nginx-ingress
      // doesn't 502). Same chunked endpoint as dry-run uses.
      await uploadInChunks(file, jobId, ({ sent, total }) => {
        setProgress({ phase: `Uploading… ${sent}/${total}`,
                       pct: Math.round((sent / total) * 100),
                       current: sent, total });
      });
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/import-projects-v2/apply?job_id=${jobId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      // The apply endpoint returns immediately with { status: "started" } — the
      // actual import runs in the background. We then poll progress until the
      // worker reports phase=Done (with result) or phase=Error (with error).
      // Read defensively (see readResponse note re: PostHog body locking).
      const { data, text } = await readResponse(res);
      const kicked = res.ok && data?.status === 'started';
      if (!res.ok && !kicked) {
        throw new Error(data?.detail || `Apply failed (HTTP ${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`);
      }
      startPolling(jobId);
      // Note: applying state stays true until polling sees Done/Error.
    } catch (e) {
      toast.error(e.message);
      setApplying(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8" data-testid="import-projects-v2-page">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-lg text-[#5B8DB8] font-light">Administration</h2>
            <h1 className="text-3xl text-gray-700 font-light italic">Import Projects v2</h1>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">
              Bulk-import legacy <strong>Closed and Dead</strong> projects from a flat RestorePM CSV export. Includes
              labor &amp; expense entries, parses PM splits, auto-creates inactive skeleton employees for legacy names,
              rounds float-noise to 2 decimals, and skips the one known collision (R26-6700-1).
              Always run Dry Run first.
            </p>
          </div>
          <button
            onClick={() => navigate('/setup')}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded border border-gray-200"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Setup
          </button>
        </div>

        <div className="bg-white rounded shadow p-6 mb-6">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="v2-file-picker">
            <span className="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded text-sm text-gray-700 flex items-center gap-2 border border-gray-300">
              <FileSpreadsheet className="w-4 h-4" /> Choose CSV
            </span>
            <input type="file" accept=".csv" onChange={onPick} className="hidden" />
            <span className="text-sm text-gray-600">{file ? file.name : 'No file selected'}</span>
          </label>

          <div className="flex gap-3 mt-4">
            <button
              onClick={doDryRun}
              disabled={!file || dryRunning || applying}
              className="bg-[#5BC0DE] hover:bg-[#46b8da] disabled:opacity-50 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
              data-testid="v2-dry-run-btn"
            >
              {dryRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
              {dryRunning ? 'Analyzing…' : 'Dry Run'}
            </button>
            <button
              onClick={doApply}
              disabled={!report || applying || dryRunning}
              className="bg-[#FFA94D] hover:bg-[#fb923c] disabled:opacity-50 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
              data-testid="v2-apply-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {applying ? 'Importing…' : 'Apply Import'}
            </button>
          </div>

          {(applying || dryRunning) && (
            <div className="mt-4" data-testid="v2-progress">
              <div className="text-sm text-gray-700 mb-1">
                {progress.phase}
                {progress.total > 0 && ` — ${progress.current}/${progress.total}`}
              </div>
              <div className="h-2 bg-gray-200 rounded overflow-hidden">
                <div
                  className="h-full bg-[#FFA94D] transition-all"
                  style={{ width: `${progress.pct || 0}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {report && (
          <div className="mb-6" data-testid="v2-report">
            <h3 className="text-lg text-gray-700 font-light italic mb-3">Dry Run Preview</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
              <Stat label="Projects in CSV" value={report.projects_in_csv} />
              <Stat label="Closed + Dead" value={report.projects_closed_dead} tone="blue" />
              <Stat label="Would Import" value={report.would_import} tone="green" />
              <Stat label="Skipped" value={(report.would_skip_collision?.length || 0) + (report.would_skip_no_customer?.length || 0)} tone="amber" />
              <Stat label="New Customers" value={report.new_customers} tone="blue" />
              <Stat label="Skeleton Employees" value={report.skeleton_employees} tone="orange" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded shadow p-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Status & Job Type Breakdown</div>
                <div className="text-xs text-gray-600 mb-3">
                  Statuses: {Object.entries(report.status_breakdown || {}).map(([k, v]) => `${k} (${v})`).join(' · ')}
                </div>
                <table className="w-full text-xs">
                  <thead className="text-gray-500 border-b">
                    <tr><th className="text-left p-1 font-normal">Job Type</th><th className="text-right p-1 font-normal">Count</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.type_breakdown || {}).map(([t, c]) => (
                      <tr key={t} className="border-b border-gray-50">
                        <td className="p-1 text-gray-700">{t || '(blank)'}</td>
                        <td className="p-1 text-right font-mono text-gray-700">{c}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white rounded shadow p-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Labor & Expense Entries</div>
                <div className="text-2xl font-light text-gray-700 mb-3">{report.entries_to_attach?.toLocaleString()}</div>
                <table className="w-full text-xs">
                  <tbody>
                    {Object.entries(report.entry_type_breakdown || {}).map(([t, c]) => (
                      <tr key={t} className="border-b border-gray-50">
                        <td className="p-1 text-gray-700">{t}</td>
                        <td className="p-1 text-right font-mono text-gray-700">{c.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(report.would_skip_collision?.length || 0) + (report.would_skip_no_customer?.length || 0) > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-4 lg:col-span-2" data-testid="v2-skips">
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-800 mb-2">
                    <AlertTriangle className="w-4 h-4" /> Will skip
                  </div>
                  {report.would_skip_collision?.length > 0 && (
                    <div className="text-xs text-amber-900 mb-2">
                      <strong>Collision:</strong> {report.would_skip_collision.join(', ')}
                    </div>
                  )}
                  {report.would_skip_no_customer?.length > 0 && (
                    <div className="text-xs text-amber-900">
                      <strong>Missing customer name:</strong> {report.would_skip_no_customer.join(', ')}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-white rounded shadow p-4 lg:col-span-2">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Skeleton employees ({report.skeleton_employees}) — created as inactive
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  Each legacy name we couldn't match in your existing directory will be added as an inactive user
                  with no email or permissions. Visible under Setup → Users &amp; Employees → Inactive tab. You can
                  activate or merge them later.
                </div>
                <div className="overflow-x-auto max-h-64 overflow-y-auto border border-gray-100 rounded">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left p-2 font-normal">Last</th>
                        <th className="text-left p-2 font-normal">First</th>
                        <th className="text-left p-2 font-normal">Mentioned In</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.skeleton_employees_sample || []).map((s, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="p-2 text-gray-700 font-mono">{s.last_name}</td>
                          <td className="p-2 text-gray-700 font-mono">{s.first_name}</td>
                          <td className="p-2 text-gray-500">{(s.mentioned_in || []).join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-5" data-testid="v2-result">
            <div className="flex items-center gap-2 text-emerald-800 font-medium mb-3">
              <CheckCircle2 className="w-5 h-5" /> Import complete
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Projects" value={result.inserted_projects} tone="green" />
              <Stat label="Customers" value={result.inserted_customers} tone="green" />
              <Stat label="Employees" value={result.inserted_employees} tone="green" />
              <Stat label="Timeslips" value={result.inserted_timeslips?.toLocaleString()} tone="green" />
            </div>
            {result.row_error_count > 0 && (
              <div className="mt-3 text-xs text-amber-700">
                {result.row_error_count} row errors — see logs for details.
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ImportProjectsV2;
